import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Briefcase, CalendarDays, ExternalLink, TrendingUp, ShieldCheck, Share2, ArrowRight, Sparkles, ChevronDown, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const REG_COLORS = {
  Regulated: "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/40",
  "In Progress": "bg-[#F59E0B]/15 text-[#fbbf24] border-[#F59E0B]/40",
  Minimal: "bg-[#94A3B8]/15 text-[#94A3B8] border-[#94A3B8]/40",
};

const Bar = ({ value, max = 100, color = "#00F0FF" }) => (
  <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
    <motion.div className="h-full rounded-full" style={{ background: color }}
      initial={{ width: 0 }} animate={{ width: `${Math.min(100, (value / max) * 100)}%` }}
      transition={{ duration: 0.8, ease: "easeOut" }} />
  </div>
);

export default function CountryPanel({ selection, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [showSources, setShowSources] = useState(false);

  useEffect(() => {
    if (!selection) return;
    setData(null);
    setSubscribed(false);
    setEmail("");
    setShowSources(false);
    if (!selection.code) {
      setData({ coming_soon: true, name: selection.name });
      return;
    }
    setLoading(true);
    api.get(`/countries/${selection.code}`)
      .then((r) => setData(r.data.coming_soon ? { coming_soon: true, name: selection.name } : r.data))
      .catch(() => setData({ coming_soon: true, name: selection.name }))
      .finally(() => setLoading(false));
    api.post(`/countries/${selection.code}/click`).catch(() => {});
  }, [selection]);

  const subscribe = async () => {
    if (!email) return;
    try {
      await api.post("/countries/subscribe", { email, country: (data && data.name) || selection?.name });
      setSubscribed(true);
      toast.success("You're on the list! We'll notify you when this country goes live.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const share = (network) => {
    const d = data;
    const text = `${d.flag || ""} ${d.name} ranks #${d.rank}${d.total_ranked ? ` of ${d.total_ranked}` : ""} on the Global AI Index — explore the world of AI on WorldAIInfo.`;
    const url = window.location.origin;
    const links = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    window.open(links[network], "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {selection && (
        <>
          {/* backdrop (mobile) */}
          <motion.div className="fixed inset-0 bg-black/50 z-[60] md:hidden"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} data-testid="country-panel-backdrop" />

          <motion.aside
            data-testid="country-panel"
            drag={window.innerWidth < 768 ? "y" : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.3}
            onDragEnd={(e, info) => { if (info.offset.y > 120) onClose(); }}
            initial={window.innerWidth < 768 ? { y: "100%" } : { x: "100%" }}
            animate={window.innerWidth < 768 ? { y: 0 } : { x: 0 }}
            exit={window.innerWidth < 768 ? { y: "100%" } : { x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed z-[61] bg-[#0B0F19] border-[#00F0FF]/20 overflow-y-auto
                       md:top-0 md:right-0 md:left-auto md:h-full md:w-[420px] md:max-h-full md:rounded-none md:border-l md:border-t-0
                       bottom-0 left-0 right-0 max-h-[85vh] rounded-t-2xl border-t">
            <div className="md:hidden flex justify-center pt-3"><div className="w-12 h-1.5 rounded-full bg-white/20" /></div>

            <div className="sticky top-0 bg-[#0B0F19]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-3xl">{data?.flag || "🌐"}</span>
                <h3 className="font-display font-bold text-xl truncate">{data?.name || selection.name}</h3>
              </div>
              <button onClick={onClose} data-testid="country-panel-close"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5">
              {loading && <div className="text-[#94A3B8] text-sm py-10 text-center">Loading intelligence…</div>}

              {data?.coming_soon && !loading && (
                <div className="text-center py-6" data-testid="country-coming-soon">
                  <div className="w-14 h-14 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-6 h-6 text-[#00F0FF]" />
                  </div>
                  <h4 className="font-display font-bold text-lg">Coming soon</h4>
                  <p className="text-sm text-[#94A3B8] mt-2">
                    We're building detailed AI intelligence for <span className="text-white font-medium">{data.name}</span>. Get notified when it's live.
                  </p>
                  {subscribed ? (
                    <div className="mt-5 text-sm text-[#4ade80] font-medium">✓ You're on the list!</div>
                  ) : (
                    <div className="mt-5 flex gap-2">
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com" data-testid="country-subscribe-input"
                        className="flex-1 bg-[#111827] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF]/60 outline-none" />
                      <button onClick={subscribe} data-testid="country-subscribe-btn"
                        className="bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2 rounded-md text-sm hover:brightness-110">Notify me</button>
                    </div>
                  )}
                </div>
              )}

              {data && !data.coming_soon && !loading && (
                <div className="space-y-6">
                  {/* rank + source */}
                  <div className="space-y-2">
                    <span className="font-mono-tech text-xs uppercase px-3 py-1 rounded-full bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30 inline-block" data-testid="country-rank">
                      Global AI Rank #{data.rank}{data.total_ranked ? ` of ${data.total_ranked}` : ""}
                    </span>
                    {data.source && (
                      <a href={data.source_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-[#64748B] hover:text-[#00F0FF] transition" data-testid="country-source">
                        Source: {data.source} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>

                  {/* capability pillar ranks (sourced) */}
                  {data.pillar_list?.length > 0 && (
                    <div>
                      <div className="text-sm text-[#94A3B8] mb-2">Capability pillar ranks <span className="text-[#64748B]">(of {data.total_ranked})</span></div>
                      <div className="grid grid-cols-2 gap-2" data-testid="country-pillars">
                        {data.pillar_list.map((p) => (
                          <div key={p.key} className="bg-[#111827] rounded-lg px-3 py-2 flex items-center justify-between">
                            <span className="text-[11px] text-[#94A3B8]">{p.label}</span>
                            <span className="font-display font-bold text-sm text-[#8B5CF6]">#{p.rank}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* live data */}
                  <div className="grid grid-cols-2 gap-3">
                    <Link to={`/jobs?country=${encodeURIComponent(data.jobs_query)}`} onClick={onClose}
                      data-testid="country-jobs-link"
                      className="bg-[#111827] rounded-lg p-4 hover:border-[#00F0FF]/40 border border-transparent transition group">
                      <Briefcase className="w-4 h-4 text-[#00F0FF] mb-2" />
                      <div className="font-display font-bold text-xl">{data.jobs_count}</div>
                      <div className="text-xs text-[#94A3B8] flex items-center gap-1 group-hover:text-[#00F0FF]">Live jobs <ArrowRight className="w-3 h-3" /></div>
                    </Link>
                    <Link to="/events" onClick={onClose} data-testid="country-events-link"
                      className="bg-[#111827] rounded-lg p-4 hover:border-[#00F0FF]/40 border border-transparent transition group">
                      <CalendarDays className="w-4 h-4 text-[#8B5CF6] mb-2" />
                      <div className="font-display font-bold text-xl">{data.events_count}</div>
                      <div className="text-xs text-[#94A3B8] flex items-center gap-1 group-hover:text-[#00F0FF]">AI events <ArrowRight className="w-3 h-3" /></div>
                    </Link>
                  </div>

                  {/* top tools */}
                  {data.top_tools?.length > 0 && (
                    <div>
                      <div className="text-sm text-[#94A3B8] mb-2">Top AI tools used</div>
                      <div className="flex flex-wrap gap-2">
                        {data.top_tools.map((t) => (
                          <span key={t} className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* regulation + strategy (only when we have sourced/verifiable data) */}
                  {data.regulation_status && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#94A3B8] flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Regulation</span>
                      <span className={`font-mono-tech text-[10px] uppercase px-2.5 py-1 rounded-full border ${REG_COLORS[data.regulation_status] || REG_COLORS.Minimal}`}>
                        {data.regulation_status}
                      </span>
                    </div>
                    {data.regulation_name && <div className="text-xs text-[#94A3B8] -mt-1">{data.regulation_name}</div>}
                    {data.has_strategy != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#94A3B8]">National AI Strategy</span>
                        <span className="text-sm font-medium">{data.has_strategy ? "Yes" : "No"}</span>
                      </div>
                    )}
                    {data.has_strategy && data.strategy_name && (
                      data.gov_link ? (
                        <a href={data.gov_link} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-[#00F0FF] flex items-center gap-1 hover:underline -mt-1" data-testid="country-strategy-link">
                          {data.strategy_name} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <div className="text-xs text-[#94A3B8] -mt-1" data-testid="country-strategy-name">{data.strategy_name}</div>
                      )
                    )}
                  </div>
                  )}

                  {/* insight */}
                  {data.insight && (
                    <div className="rounded-xl border border-[#00F0FF]/20 bg-[#00F0FF]/5 p-4">
                      <div className="font-mono-tech text-[10px] uppercase text-[#00F0FF] mb-1.5 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Key AI insight
                      </div>
                      <p className="text-sm text-white/90 leading-relaxed" data-testid="country-insight">{data.insight}</p>
                    </div>
                  )}

                  {/* share */}
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs text-[#94A3B8] flex items-center gap-1"><Share2 className="w-3.5 h-3.5" /> Share</span>
                    <button onClick={() => share("twitter")} data-testid="country-share-twitter"
                      className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:border-[#00F0FF]/50 transition">Twitter / X</button>
                    <button onClick={() => share("linkedin")} data-testid="country-share-linkedin"
                      className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:border-[#00F0FF]/50 transition">LinkedIn</button>
                  </div>

                  {/* Collapsible data sources */}
                  {data.citations?.length > 0 && (
                    <div className="pt-2 border-t border-white/10">
                      <button onClick={() => setShowSources((v) => !v)} data-testid="country-sources-toggle"
                        className="flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-[#00F0FF] transition">
                        <FileText className="w-3.5 h-3.5" /> View data sources for this country
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSources ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {showSources && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden" data-testid="country-sources-list">
                            <div className="mt-3 space-y-3">
                              {data.citations.map((cit, i) => (
                                <div key={i} className="rounded-lg bg-[#111827] border border-white/10 p-3" data-testid="country-citation-row">
                                  <div className="text-sm font-medium">{cit.label}: <span className="text-[#00F0FF]">{cit.value}</span></div>
                                  <div className="text-xs text-[#94A3B8] mt-0.5">
                                    {cit.organisation}{cit.report_name ? ` — ${cit.report_name}` : ""}{cit.date ? ` — ${cit.date}` : ""}
                                  </div>
                                  {cit.note && <div className="text-[11px] text-[#64748B] mt-1 italic">{cit.note}</div>}
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {cit.links.filter((l) => l.url).map((l, j) => (
                                      <a key={j} href={l.url} target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10 transition">
                                        {l.label} <ExternalLink className="w-3 h-3" />
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Verification footer */}
                  {data.citation_footer && (
                    <p className="text-[11px] text-[#64748B] leading-relaxed pt-1" data-testid="country-citation-footer">
                      {data.citation_footer.split("worldaiinfo.com/sources")[0]}
                      <Link to="/sources" onClick={onClose} className="text-[#00F0FF] hover:underline">worldaiinfo.com/sources</Link>
                    </p>
                  )}
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
